import type { StatementImportOperationStatusV2 } from "@tradvera/contracts";
import { finalizePendingStatementOperationV2, readPendingStatementOperationV2,
  samePendingStatementOperationV2, withPendingStatementOperationLockV2,
  type PendingStatementOperationV2 } from "./pending-operation";
import type { DurableStatementUploadStorageV2 } from "./durable-upload";
import { prepareBrowserStatementUploadV2, type StatementUploadChoiceV2 } from "./upload";

export type StatementUploadQueueItemV2 = { file: File; choice: StatementUploadChoiceV2 };

export async function findStatementUploadQueuePendingV2(
  queued: readonly StatementUploadQueueItemV2[],
  bookId: string,
  marker: Pick<PendingStatementOperationV2, "idempotencyKey" | "inputFingerprint">,
): Promise<{ pending: StatementUploadQueueItemV2; remaining: StatementUploadQueueItemV2[] } | null> {
  for (let index = 0; index < queued.length; index += 1) {
    const candidate = queued[index];
    if (!candidate) continue;
    const prepared = await prepareBrowserStatementUploadV2({ ...candidate, bookId,
      idempotencyKey: marker.idempotencyKey });
    if (prepared.inputFingerprint === marker.inputFingerprint) {
      return { pending: candidate, remaining: queued.filter((_, itemIndex) => itemIndex !== index) };
    }
  }
  return null;
}

/** A multi-file retry must replay the exact pending file first. The match is content-bound,
 * not filename-bound, and preserves the pending idempotency key. */
export async function alignStatementUploadQueueWithPendingV2(
  queued: readonly StatementUploadQueueItemV2[],
  bookId: string,
  marker: Pick<PendingStatementOperationV2, "idempotencyKey" | "inputFingerprint">,
): Promise<{ pending: StatementUploadQueueItemV2; remaining: StatementUploadQueueItemV2[] }> {
  const match = await findStatementUploadQueuePendingV2(queued, bookId, marker);
  if (match) return match;
  throw new Error("STATEMENT_IMPORT_PENDING_OPERATION_INPUT_MISMATCH");
}

/** Retire only a locally stranded upload marker, never server truth. A second status
 * read closes the race with a late response before the newly selected bundle starts. */
export async function retireMissingPendingUploadForNewBundleV2(options: {
  storage: DurableStatementUploadStorageV2;
  ownerId: string;
  marker: PendingStatementOperationV2;
  confirmStatus(): Promise<StatementImportOperationStatusV2>;
  nowEpochMs?: number;
  minimumUncertainAgeMs?: number;
}): Promise<void> {
  if (options.marker.operation !== "UPLOAD") pendingError("OTHER_OPERATION");
  const age = options.marker.createdAtEpochMs === undefined ? Number.POSITIVE_INFINITY
    : (options.nowEpochMs ?? Date.now()) - options.marker.createdAtEpochMs;
  if (!Number.isFinite(age) && age !== Number.POSITIVE_INFINITY) pendingError("INVALID");
  if (age < (options.minimumUncertainAgeMs ?? 15_000)) pendingError("PENDING");
  await withPendingStatementOperationLockV2(options.ownerId, options.marker.bookId, async () => {
    const current = readPendingStatementOperationV2(
      options.storage, options.ownerId, options.marker.bookId);
    if (current.state !== "VALID" || !samePendingStatementOperationV2(current.marker, options.marker)) {
      pendingError(current.state === "VALID" ? "REPLACED" : current.state);
    }
    const status = await options.confirmStatus();
    if (status.operation !== "UPLOAD" || status.bookId !== options.marker.bookId
      || status.idempotencyKey !== options.marker.idempotencyKey
      || status.inputFingerprint !== options.marker.inputFingerprint) {
      pendingError("STATUS_BINDING_MISMATCH");
    }
    if (status.state !== "NOT_FOUND") pendingError(
      status.state === "PENDING" ? "PENDING" : "STATUS_CHANGED");
    const finalized = finalizePendingStatementOperationV2(
      options.storage, options.ownerId, options.marker);
    if (finalized.state !== "ABSENT") pendingError(
      finalized.state === "REPLACED" ? "REPLACED" : finalized.state);
  });
}

function pendingError(reason: string): never {
  throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${reason}`);
}
